import { useQueryClient } from '@tanstack/react-query'
import { ChevronLeft, Trash2 } from 'lucide-react'
import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { EmptyState } from '../../components/EmptyState.tsx'
import { Obj } from '../../components/Obj.tsx'
import { Busy, SkCard } from '../../components/Skeleton.tsx'
import { useToast } from '../../lib/toast.ts'
import { MutiaraCardBody } from './MutiaraCardBody.tsx'
import { JENIS_INFO, jenisTint, postSaved, useKotak, type MutiaraCard } from './mutiara.ts'

const UNDO_MS = 6000

/** /mutiara/kotak: saved Mutiara cards (docs/PRD.md US-11.1); remove with "Urungkan" (UX-PLAYBOOK §10). */
export function KotakPage() {
  const { data, isPending, isError, refetch } = useKotak()
  const qc = useQueryClient()
  const [hidden, setHidden] = useState<Set<string>>(new Set())
  const [undo, setUndo] = useState<MutiaraCard | null>(null)
  const [toast, show] = useToast()
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined)

  const pending = useRef<MutiaraCard | null>(null)
  const unhide = (id: string) => setHidden((s) => { const n = new Set(s); n.delete(id); return n })

  /** Removed for real when the undo window closes (or when the next card is removed). */
  const commit = async () => {
    const c = pending.current
    if (!c) return
    clearTimeout(timer.current)
    pending.current = null
    setUndo(null)
    try {
      await postSaved(c.id, false)
      void qc.invalidateQueries({ queryKey: ['mutiara'] })
    } catch {
      unhide(c.id)
      show('Belum bisa dihapus. Cek koneksi, lalu coba lagi.')
    }
  }
  const remove = (c: MutiaraCard) => {
    void commit()
    pending.current = c
    setHidden((s) => new Set(s).add(c.id))
    setUndo(c)
    timer.current = setTimeout(() => void commit(), UNDO_MS)
  }
  const restore = () => {
    clearTimeout(timer.current)
    if (pending.current) unhide(pending.current.id)
    pending.current = null
    setUndo(null)
  }

  const cards = data?.cards.filter((c) => !hidden.has(c.id)) ?? []
  return (
    <div className="page">
      <Link className="back" to="/mutiara"><ChevronLeft size={18} aria-hidden="true" /> Mutiara</Link>
      <h1>Kotak Mutiara</h1>
      <p className="muted" style={{ marginTop: -6 }}>Kartu yang kamu simpan untuk dibaca lagi.</p>

      {isPending && <Busy label="Memuat kartu" className="stack" style={{ marginTop: 14 }}>{[0, 1].map((i) => <SkCard key={i} lines={3} h={160} />)}</Busy>}
      {isError && (
        <EmptyState obj="jam-pasir" title="Belum bisa dimuat" action={<button className="btn btn-secondary" onClick={() => refetch()}>Coba lagi</button>}>
          Koneksi lagi bermasalah. Cek internet kamu, lalu coba lagi.
        </EmptyState>
      )}
      {data && cards.length === 0 && (
        <EmptyState obj="kado" title="Kotakmu masih kosong" action={<Link className="btn btn-ghost" to="/mutiara">Buka Mutiara</Link>}>
          Tekan Simpan pada kartu Mutiara yang ingin kamu baca lagi.
        </EmptyState>
      )}
      {cards.length > 0 && (
        <div className="stack" style={{ marginTop: 14 }}>
          {cards.map((c) => (
            <article key={c.id} className="kt-card" style={{ background: jenisTint(c.jenis) }} aria-label={JENIS_INFO[c.jenis].label}>
              <header className="mt-head">
                <Obj id={JENIS_INFO[c.jenis].obj} size={36} />
                <span className="eyebrow">{JENIS_INFO[c.jenis].label}</span>
                <button type="button" className="icon-btn plain kt-del" onClick={() => remove(c)} aria-label={`Hapus dari Kotak: ${JENIS_INFO[c.jenis].label}`}>
                  <Trash2 size={18} aria-hidden="true" />
                </button>
              </header>
              <MutiaraCardBody c={c} />
            </article>
          ))}
        </div>
      )}

      <div className="toast-slot" role="status">
        {undo ? (
          <p className="toast kt-undo">Terhapus. <button type="button" onClick={restore}>Urungkan</button></p>
        ) : (
          toast && <p className="toast">{toast}</p>
        )}
      </div>
    </div>
  )
}
